import React, { FC, MouseEventHandler, useCallback } from 'react'
import classNames from 'classnames'
import copy from 'copy-to-clipboard'
import CheckFilledIcon from 'transfix-icons/build/CheckFilledIcon'
import WarningFilledIcon from 'transfix-icons/build/WarningFilledIcon'
import { Button } from 'transfix-ui/components/Button'
import { Rating } from 'transfix-ui/components/Rating'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Link } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { ICarrierApplicationStates, Maybe } from 'types/graphqlTypes'
import { getNewCarrierStatus } from 'utils/carriers/getNewCarrierStatus'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { AlertIcon, CopyIcon } from 'transfix-icons'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui'
import DISPLAY from 'constants/display'
import { useExtendedCarrierScorecardStyles } from './hooks/useExtendedCarrierScorecardStyles'
import { CARRIER_STATE_LABELS, ERateRank, RATE_LABELS, RATE_STYLE } from './types'

export interface IExtendedScorecardProps {
  className?: string
  carrierName: string
  status: ICarrierApplicationStates
  rate: ERateRank
  carrierScore: Maybe<number>
  shipmentsCount?: Maybe<number>
  bounceRate: Maybe<number>
  onTimeRate: Maybe<number>
  trackingRate: Maybe<number>
  mcNumber: Maybe<string>
  dotNumber: Maybe<string>
  hqValue: Maybe<string>
  carrierId?: string
  carrierLinkPosition?: 'top' | 'bottom' | 'none'
  reduced?: boolean
}

const stopClickPropagation: MouseEventHandler<HTMLElement> = event => event.stopPropagation()

export const ExtendedScorecard: FC<IExtendedScorecardProps> = React.memo(
  ({
    bounceRate: bounceRateProp,
    carrierId,
    carrierLinkPosition = 'bottom',
    carrierName,
    carrierScore: carrierScoreProp,
    className = '',
    dotNumber,
    hqValue,
    mcNumber,
    onTimeRate: onTimeRateProp,
    rate: rateProp,
    reduced = false,
    shipmentsCount = 0,
    status,
    trackingRate: trackingRateProp,
  }) => {
    const isUseNewStateTagsMpEnabled = useFeatureFlag('useNewStateTagsMp')

    const classes = useExtendedCarrierScorecardStyles()
    const { openToast } = useGlobalToast()

    const copyValueHandler: (
      value: Maybe<string>,
      fieldName: string
    ) => MouseEventHandler<HTMLElement> = useCallback(
      (value, fieldName) => event => {
        event.preventDefault()
        event.stopPropagation()
        if (value) {
          copy(value)
          openToast({
            toastType: 'default',
            toastMessage: `${fieldName} copied`,
          })
        }
      },
      [openToast]
    )

    const carrierURL = `${__ROOT__}/carriers/${carrierId}`
    const carrierDetailsHandler = useCallback(() => {
      window.open(carrierURL, '_blank')
    }, [carrierId])

    const isActive = status === ICarrierApplicationStates.activated
    const showCarrierLinkOnTop = carrierLinkPosition === 'top' && carrierId
    const showCarrierLinkOnBottom = carrierLinkPosition === 'bottom' && carrierId

    const carrierScore = typeof carrierScoreProp === 'number' ? carrierScoreProp : DISPLAY.emptyDash
    const bounceRate = typeof bounceRateProp === 'number' ? `${bounceRateProp}%` : DISPLAY.empty
    const onTimeRate = typeof onTimeRateProp === 'number' ? `${onTimeRateProp}%` : DISPLAY.empty
    const trackingRate =
      typeof trackingRateProp === 'number' ? `${trackingRateProp}%` : DISPLAY.empty
    const showScoreTag = carrierScoreProp && RATE_STYLE[rateProp]

    return (
      <Box
        className={classNames(classes.root, className)}
        onClick={stopClickPropagation}
        data-test='ExtendedCarrierScorecard'>
        {!reduced && (
          <>
            {showCarrierLinkOnTop ? (
              <Link
                className='title'
                variant='body1'
                href={carrierURL}
                data-test='carrier-detail-link'>
                {carrierName}
              </Link>
            ) : (
              <Typography variant='h3'>{carrierName}</Typography>
            )}
            <div className={classNames(classes.carrierState, status)}>
              {isActive ? <CheckFilledIcon /> : <WarningFilledIcon />}
              <Typography variant='subtitle2'>
                {getNewCarrierStatus(CARRIER_STATE_LABELS[status], isUseNewStateTagsMpEnabled)}
              </Typography>
            </div>
          </>
        )}
        <div className={classes.rateInfo}>
          <Rating size='xlarge' value={carrierScoreProp} defaultValue={0} precision={0.5} />
          <Typography className='score' variant='body1'>
            {carrierScore}
          </Typography>
          {showScoreTag && (
            <Tag
              className={classes.rateTag}
              label={RATE_LABELS[rateProp]}
              size={TAG_SIZE.small}
              tagStyle={RATE_STYLE[rateProp] as TAG_STYLE}
            />
          )}
        </div>
        {!reduced && (
          <div className={classes.shipmentsLabel}>
            <Typography component='span' variant='subtitle1'>
              {shipmentsCount}
            </Typography>
            <span className='label'>
              <Typography component='span' variant='subtitle1'>
                {' '}
                Total Shipments{' '}
              </Typography>
              <Tooltip position='bottom' trigger={<AlertIcon />}>
                Overall grade is based on the carrier&apos;s 50 most recent shipments within the
                past year.
              </Tooltip>
            </span>
          </div>
        )}
        <div className={classes.statsInfo}>
          <span>
            <Typography variant='subtitle1'>{onTimeRate}</Typography>
            <Typography className='label' variant='caption'>
              On Time
            </Typography>
          </span>
          <span>
            <Typography variant='subtitle1'>{trackingRate}</Typography>
            <Typography className='label' variant='caption'>
              Tracking
            </Typography>
          </span>
          <span>
            <Typography variant='subtitle1'>{bounceRate}</Typography>
            <Typography className='label' variant='caption'>
              Bounce
            </Typography>
          </span>
        </div>
        {reduced && (
          <div className={classes.countMessage}>
            <AlertIcon className='icon' />
            <Typography component='span' variant='caption'>
              Overall grade is based on the carrier&apos;s 50 most recent shipments within the past
              year.
            </Typography>
          </div>
        )}
        {!reduced && (
          <div className={classes.infoRows}>
            <div className={classes.infoRow}>
              <Typography variant='body2'>MC</Typography>
              <span>
                {mcNumber}
                {mcNumber && (
                  <Tooltip
                    id='copy-mc'
                    position='bottom'
                    effect='solid'
                    trigger={
                      <Box
                        display='flex'
                        alignItems='center'
                        component='span'
                        onClick={copyValueHandler(mcNumber, 'MC number')}
                        data-test='copy-mc-button'>
                        <CopyIcon color='info' />
                      </Box>
                    }>
                    <span>Copy MC Number</span>
                  </Tooltip>
                )}
              </span>
            </div>
            <div className={classes.infoRow}>
              <Typography variant='body2'>DOT</Typography>
              <span>
                {dotNumber}
                {dotNumber && (
                  <Tooltip
                    id='copy-dot'
                    position='bottom'
                    effect='solid'
                    trigger={
                      <Box
                        display='flex'
                        alignItems='center'
                        component='span'
                        onClick={copyValueHandler(dotNumber, 'DOT number')}
                        data-test='copy-dot-button'>
                        <CopyIcon color='info' />
                      </Box>
                    }>
                    <span>Copy DOT Number</span>
                  </Tooltip>
                )}
              </span>
            </div>
            <div className={classes.infoRow}>
              <Typography variant='body2'>HQ</Typography>
              <span>{hqValue}</span>
            </div>
          </div>
        )}
        {showCarrierLinkOnBottom && (
          <Button className={classes.button} color='primary' onClick={carrierDetailsHandler}>
            View Carrier Profile
          </Button>
        )}
      </Box>
    )
  }
)

export default ExtendedScorecard
